import React, { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSetHeaderTitle } from '../../hooks/hooks'
import { retrieveErrorMessage, setPageTitle } from '../../utils/helpers';
import { useAddScoreboardParticipantMutation, useCreateScoreboardResultMutation, useDeleteScoreboardMutation, useFetchScoreboardDetailQuery, useFetchScoreboardResultsQuery, useRemoveScoreboardParticipantMutation } from '../../services/api';
import { useUiStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { ParticipantFormValues } from '../Dashboard/PlanActivity/components/AddParticipantModal';
import { routes } from '../../utils/constants'

const useScoreboard = () => {
  useSetHeaderTitle('Scoreboard', true)
  setPageTitle('Scoreboard')
  
  const {scoreboard_id} = useParams()
  const parsedScoreboardId = Number(scoreboard_id)
  const [resultsPage, setResultsPage] = React.useState(1)
  const navigate = useNavigate()
  const { currentUser, isLoggedIn, loadUserIfNecessary } = useAuthStore()
  const {isLoading, isError, data} = useFetchScoreboardDetailQuery({id: parsedScoreboardId})
  const [addScoreboardParticipant, {isLoading: isAddingParticipant}] = useAddScoreboardParticipantMutation()
  const [removeScoreboardParticipant, {isLoading: isRemovingParticipant}] = useRemoveScoreboardParticipantMutation()
  const [createScoreboardResult, {isLoading: isCreatingScoreboardResult}] = useCreateScoreboardResultMutation()
  const [deleteScoreboard] = useDeleteScoreboardMutation()
  const isReady = !isLoading && !isError && !!data?.payload
  const { cancelDialog, showConfirmDialog, openErrorToast } = useUiStore()
  const {data: results, isLoading: isLoadingScoreboardResults, isError: isErrorScoreboardResults} = useFetchScoreboardResultsQuery({
    id: parsedScoreboardId,
    page: resultsPage,
  })

  useEffect(() => {
    loadUserIfNecessary()
  }, [loadUserIfNecessary])

  const onParticipantAdded = (participants: ParticipantFormValues[]) => {
    if (participants.length === 0) return
    addScoreboardParticipant({
      id: parsedScoreboardId,
      user_id: participants[0].username?.username,
      name: participants[0].name,
      contact: participants[0].contact
    })
    cancelDialog()
  }

  const removeParticipant = (participantId: number) => {
    showConfirmDialog({
      dialogBody: 'Are you sure you want to remove this participant?',
      onConfirmAction: () => {
        removeScoreboardParticipant({id: parsedScoreboardId, participant_id: participantId})
      }
    })
  }

  const onDeleteScoreboard = () => {
    showConfirmDialog({
      dialogTitle: 'Delete scoreboard',
      dialogBody: 'Are you sure you want to delete this scoreboard? It cannot be undone.',
      onConfirmAction: async () => {
        try {
          await deleteScoreboard({ id: parsedScoreboardId }).unwrap()
          navigate(routes.SCOREBOARD_INDEX)
        } catch (e) {
          openErrorToast({ message: retrieveErrorMessage(e) || 'Failed to delete scoreboard' })
        }
      },
    })
  }

  const onCreateScoreboardResult = async (label: string) => {
    try {
      await createScoreboardResult({
        scoreboard_id: parsedScoreboardId,
        page: resultsPage,
        label,
      }).unwrap()
      cancelDialog()
    } catch (e) {
      openErrorToast({ message: retrieveErrorMessage(e) || 'Failed to create scoreboard result' })
    }
  }

  const creator = data?.payload.creator
  const participants = data?.payload.scoreboard_participants
  const hasParticipants = participants?.some((participant) => participant.username !== creator?.username) || false
  const creatorUsername = creator?.username

  const hasWriteAccess = useMemo(() => {
    if (!isLoggedIn || !currentUser.username) return false
    if (!data?.payload) return false

    if (creator?.username === currentUser.username) return true

    return (participants ?? []).some(
      (participant) => participant.username === currentUser.username
    )
  }, [isLoggedIn, currentUser.username, data?.payload, creator?.username, participants])
  const scoreboardResults = useMemo(() => results?.payload || [], [results])
  const resultsPaginationInfo = results?.pagination_info
  const hasPreviousResultsPage = resultsPage > 1
  const hasNextResultsPage = !!resultsPaginationInfo?.next_page

  const defaultNewResultLabel = useMemo(() => {
    const lastResultLabel = scoreboardResults[0]?.label
    if (!lastResultLabel) return 'Game 1'
    const lastResultNumber = parseInt(lastResultLabel.match(/\d+/)?.[0] || '1')
    return `Game ${lastResultNumber + 1}`
  }, [scoreboardResults])

  const gotoNextResultsPage = () => {
    if (!resultsPaginationInfo?.next_page) return
    setResultsPage(resultsPaginationInfo.next_page)
  }

  const gotoPreviousResultsPage = () => {
    if (!hasPreviousResultsPage) return
    setResultsPage(prev => Math.max(1, prev - 1))
  }

  return {
    isLoading,
    isError,
    data,
    isReady,
    onParticipantAdded,
    isAddingParticipant,
    isRemovingParticipant,
    isCreatingScoreboardResult,
    onDeleteScoreboard,
    onCreateScoreboardResult,
    removeParticipant,
    hasParticipants,
    hasWriteAccess,
    creatorUsername,
    scoreboardResults,
    resultsPage,
    resultsPaginationInfo,
    hasPreviousResultsPage,
    hasNextResultsPage,
    gotoNextResultsPage,
    gotoPreviousResultsPage,
    isLoadingScoreboardResults,
    isErrorScoreboardResults,
    defaultNewResultLabel,
    scoreboard_id: parsedScoreboardId
  }
}

export default useScoreboard